import { URLData } from '@telia-common-components/shared';
import React from 'react';
import { useAnalytics } from 'telia-front-react';
import API from '../../../util/ApiUtil';
import ErrorUtil from '../../../util/ErrorUtil';
import { FetchStatus } from '../../../util/FetchStatus';
import { useInternetCalculator } from './InternetCalculatorProvider';
import { getCustomerIdFromAuth, getIsDealerFromAuth } from './util/InternetCalculatorUtil';
import { addFix4gExceptionOfferCardIfNecessary, createNewState, joinOfferNames } from './util/InternetOfferUtil';
import { internetSuggestSpeedCalculatorDefaultState } from './suggest/InternetSuggestSpeedUtil';
const addressProductsInitialState = {
    status: FetchStatus.UNINITIALIZED,
    addressProducts: undefined,
};
export const InternetOffersProvider = (props) => {
    const { push } = useAnalytics();
    const [addressProductsState, setAddressProductsState] = React.useState(addressProductsInitialState);
    const [calculatorState, setCalculatorState] = React.useState(createNewState({
        onAddressSelect: props.onAddressSelect,
        labelsPrefix: props.labelsPrefix || '',
        roomLabel: props.roomLabel,
        address: props.address,
        room: props.room,
    }));
    const { auth, setSuggestSpeedState, isSecureNet } = useInternetCalculator();
    const isDealer = getIsDealerFromAuth(auth);
    const customerId = getCustomerIdFromAuth(auth);
    const urlData = new URLData({ ...props, lang: props.language });
    const commonApi = API(urlData.commonAPIBaseURL, {
        withCredentials: true,
        timeout: 20000,
    });
    React.useEffect(() => {
        setCalculatorState((prevState) => ({
            ...prevState,
            ...createNewState({
                onAddressSelect: props.onAddressSelect,
                labelsPrefix: props.labelsPrefix || '',
                roomLabel: props.roomLabel,
                address: props.address,
                room: props.room,
            }),
        }));
    }, []);
    const onAddressChange = async (address, room) => {
        var _a;
        setSuggestSpeedState(internetSuggestSpeedCalculatorDefaultState);
        setCalculatorState((prevState) => ({
            ...prevState,
            address,
            room,
        }));
        (_a = calculatorState.onAddressSelect) === null || _a === void 0 ? void 0 : _a.call(calculatorState, address, room);
        await fetchOffersByLocation(address, room);
    };
    const isInvalidAddress = (address, room) => {
        var _a;
        return (!address || ((_a = address === null || address === void 0 ? void 0 : address.rooms) === null || _a === void 0 ? void 0 : _a.length)) && !(room === null || room === void 0 ? void 0 : room.roomId);
    };
    const fetchAddressProducts = async (address, room) => {
        if (!customerId || isInvalidAddress(address, room)) {
            addressProductsState !== addressProductsInitialState && setAddressProductsState(addressProductsInitialState);
            return;
        }
        setAddressProductsState((prevState) => ({
            ...prevState,
            status: FetchStatus.FETCHING,
        }));
        try {
            const { data } = await commonApi.get('/product/address-products', {
                params: {
                    addressId: address.id,
                    buildingId: address.buildingId,
                    roomId: room === null || room === void 0 ? void 0 : room.roomId,
                },
            });
            setAddressProductsState({
                addressProducts: data,
                status: FetchStatus.FETCHED,
            });
        }
        catch (e) {
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
            setAddressProductsState({
                addressProducts: undefined,
                status: FetchStatus.ERROR,
            });
        }
    };
    const fetchOffersByLocation = async (address, room) => {
        fetchAddressProducts(address, room);
        if (isInvalidAddress(address, room)) {
            return;
        }
        try {
            setCalculatorState((prevState) => ({
                ...prevState,
                status: FetchStatus.FETCHING,
            }));
            const { data } = await commonApi.get('/internet-offers', {
                params: {
                    addressId: address.id,
                    buildingId: address.buildingId,
                    roomId: room === null || room === void 0 ? void 0 : room.roomId,
                    roomName: room === null || room === void 0 ? void 0 : room.roomName,
                    segment: isSecureNet ? 'business' : 'private',
                },
            });
            data.internetOfferCards = addFix4gExceptionOfferCardIfNecessary(data.internetOfferCards, isDealer, urlData, address, room);
            setCalculatorState((prevState) => ({
                ...prevState,
                internetOffers: data,
                showRequestForm: data.showRequestForm,
                status: FetchStatus.FETCHED,
            }));
            push(`Internets shown to user: ${joinOfferNames(data)}`);
        }
        catch (e) {
            ErrorUtil.pushErrorToTeliaRumAndLog(e);
            setCalculatorState((prevState) => ({
                ...createNewState(prevState),
                status: FetchStatus.ERROR,
            }));
        }
    };
    return (React.createElement(InternetOffersContext.Provider, { value: {
            calculatorState,
            fetchOffersByLocation,
            addressProductsState,
            onAddressChange,
        } }, props.children));
};
export const InternetOffersContext = React.createContext({
    calculatorState: undefined,
    fetchOffersByLocation: (_address, _room) => undefined,
    addressProductsState: addressProductsInitialState,
    onAddressChange: (_address, _room) => undefined,
});
export const useInternetOffers = () => {
    return React.useContext(InternetOffersContext);
};
